import { ContactService } from "../services/contactService.js"

export function normalizeNumCopies(value) {
    const n = Math.floor(Number(value))
    return Number.isFinite(n) && n >= 1 ? n : 1
}

export function buildLabelHelpers(helpers) {
    const address = helpers?.address ?? ((order) => order?.ship_to ?? order?.bill_to ?? order ?? {})
    const isOverseas = helpers?.isOverseas ?? ((order) => {
        const country = String(address(order)?.country ?? "").trim().toLowerCase()
        return Boolean(country && country !== "usa" && country !== "us" && country !== "united states" && country !== "united states of america")
    })
    const getOriginalLocation = helpers?.getOriginalLocation ?? ((order, locationsList) => {
        const id = order?.original_location_id ?? order?.location_id ?? null
        if (!id) {
            return null
        }
        return (locationsList ?? []).find((location) => location.id === id) ?? null
    })
    const formatFishTag = helpers?.formatFishTag ?? ((order) => (
        order?.fish_tag ? `Fish Tag: ${order.fish_tag}` : ""
    ))

    function labelStatus(order) {
        if (order?.hold_for_pick_up) {
            return "HOLD FOR PU"
        }
        if (isOverseas(order)) {
            return "OVERSEAS"
        }
        if (order?.ship_only) {
            return "SHIP ONLY"
        }
        return ""
    }

    return { address, getOriginalLocation, labelStatus, formatFishTag }
}

/** master,last,first,location,status,boxNum of total */
export function qrCodeValue(order, boxNum, locationCode, status) {
    const name = ContactService.getPrimaryName(order)
    const lastName = String(name.last ?? "")
    const firstName = String(name.first ?? "")
    const totalBoxes = Math.max(0, Math.floor(Number(order?.total_boxes) || 0))
    return `${order?.master ?? ""},${lastName},${firstName},${locationCode},${status},${boxNum} of ${totalBoxes}`.toUpperCase()
}

/** @returns {object[]} */
export function buildLabelPages(orders, locations, helpers) {
    const { getOriginalLocation, labelStatus, formatFishTag } = buildLabelHelpers(helpers)
    const labels = []

    for (const order of orders ?? []) {
        const totalBoxes = Math.max(0, Math.floor(Number(order?.total_boxes) || 0))
        const locationCode = getOriginalLocation(order, locations)?.weigh_in_code ?? ""
        const customerName = ContactService.getPrimaryName(order).lastCommaFirst().toUpperCase()
        const status = labelStatus(order)
        const fishTag = formatFishTag(order)

        for (let boxNum = 1; boxNum <= totalBoxes; boxNum++) {
            labels.push({
                master: String(order?.master ?? ""),
                customerName,
                status,
                fishTag,
                fishTagRaw: String(order?.fish_tag ?? "").trim(),
                tripCode: String(order?.trip_code ?? "").trim(),
                fishingLicense: String(order?.fishing_license ?? "").trim(),
                locationCode,
                boxNum,
                totalBoxes,
                qrValue: qrCodeValue(order, boxNum, locationCode, status),
            })
        }
    }

    return labels
}

/**
 * Expand label pages with copies, grouped per order (for receipt interleaving).
 *
 * @returns {{ labelList: object[], segments: { pageCount: number, order: any }[] }}
 */
export function buildLabelPrintPlan(orders, locations, helpers, copies) {
    const { getOriginalLocation, labelStatus, formatFishTag } = buildLabelHelpers(helpers)
    const labelList = []
    const segments = []

    for (const order of orders ?? []) {
        const totalBoxes = Math.max(0, Math.floor(Number(order?.total_boxes) || 0))
        const locationCode = getOriginalLocation(order, locations)?.weigh_in_code ?? ""
        const customerName = ContactService.getPrimaryName(order).lastCommaFirst().toUpperCase()
        const status = labelStatus(order)
        const fishTag = formatFishTag(order)
        const orderPages = []

        for (let boxNum = 1; boxNum <= totalBoxes; boxNum++) {
            const page = {
                master: String(order?.master ?? ""),
                customerName,
                status,
                fishTag,
                fishTagRaw: String(order?.fish_tag ?? "").trim(),
                tripCode: String(order?.trip_code ?? "").trim(),
                fishingLicense: String(order?.fishing_license ?? "").trim(),
                locationCode,
                boxNum,
                totalBoxes,
                qrValue: qrCodeValue(order, boxNum, locationCode, status),
            }
            for (let c = 0; c < copies; c++) {
                orderPages.push(page)
            }
        }

        labelList.push(...orderPages)
        segments.push({
            pageCount: orderPages.length,
            order,
        })
    }

    return { labelList, segments }
}
